import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  split,
} from '@apollo/client';
import { SetContextLink } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import { env } from '../../config/env.js';
import { getAuthHeaders, getConnectionParams } from '../auth/headers.js';
import { useConnectionStore } from '../stores/connectionStore.js';
import { errorHandler } from './links/errorLink.js';

const authLink = new SetContextLink((prevContext) => ({
  headers: {
    ...prevContext.headers,
    ...getAuthHeaders(),
  },
}));

const httpLink = new HttpLink({
  uri: `${env.apiUrl}/graphql`,
});

let wsLink = null;

if (typeof window !== 'undefined') {
  wsLink = new GraphQLWsLink(
    createClient({
      url: `${env.wsUrl}/subscriptions`,
      connectionParams: () => getConnectionParams(),
      retryAttempts: Infinity,
      shouldRetry: () => true,
      retryWait: async (retries) => {
        const delay = Math.min(1000 * 2 ** retries, 30000);
        useConnectionStore.getState().setWsStatus('reconnecting');
        await new Promise((r) => setTimeout(r, delay));
      },
      on: {
        connected: () => useConnectionStore.getState().setWsStatus('connected'),
        closed: () => useConnectionStore.getState().setWsStatus('disconnected'),
        error: () => useConnectionStore.getState().setWsStatus('reconnecting'),
      },
    })
  );
}

const retryLink = new RetryLink({
  delay: { initial: 300, max: 5000, jitter: true },
  attempts: { max: 3, retryIf: (error) => !!error && !('statusCode' in error && error.statusCode === 401) },
});

const errorLink = onError(errorHandler);

const splitLink =
  wsLink &&
  split(
    ({ query }) => {
      const def = getMainDefinition(query);
      return def.kind === 'OperationDefinition' && def.operation === 'subscription';
    },
    wsLink,
    ApolloLink.from([authLink, errorLink, retryLink, httpLink])
  );

export const apolloClient = new ApolloClient({
  link: splitLink || ApolloLink.from([authLink, errorLink, retryLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          feedList: {
            keyArgs: ['categories'],
            merge(existing, incoming, { args }) {
              const skip = args?.skip ?? 0;
              if (!existing || skip === 0) {
                return incoming;
              }
              return {
                ...incoming,
                items: [...(existing.items || []), ...(incoming.items || [])],
              };
            },
          },
          notifications: {
            keyArgs: false,
            merge(existing, incoming) {
              if (!existing) return incoming;
              return {
                ...incoming,
                items: [...(existing.items || []), ...(incoming.items || [])],
              };
            },
          },
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: { fetchPolicy: 'cache-and-network' },
    query: { fetchPolicy: 'network-only', errorPolicy: 'all' },
    mutate: { errorPolicy: 'all' },
  },
});
